import { AssessmentDraftingAnalytics } from './AssessmentDraftingAnalytics.js'

const DEFAULT_STAGE_LABELS = [
  'Recognize cues',
  'Analyze cues',
  'Prioritize hypotheses',
  'Generate solutions',
  'Take action',
  'Evaluate outcomes',
]

const FORMAT_LABELS = {
  multiplechoice: 'Multiple Choice',
  multiplechoice_multi: 'Multiple-answer Multiple Choice',
  'ngn-matrix': 'Matrix',
  'ngn-dropdown-table': 'Dropdown Table',
  ngn_dropdown: 'Dropdown',
  'ngn-grouping': 'Grouping',
  'ngn-bowtie': 'Bowtie',
}

export class ClinicalCaseDraftingUI {
  static mount(config) {
    window.MyEducator = window.MyEducator || {}
    if (window.MyEducator.clinicalCaseDrafting) {
      window.MyEducator.clinicalCaseDrafting.destroy()
    }
    window.MyEducator.clinicalCaseDrafting = new ClinicalCaseDraftingUI(config)
    window.MyEducator.clinicalCaseDrafting.attachSetup()
    return window.MyEducator.clinicalCaseDrafting
  }

  static resume(config) {
    window.MyEducator = window.MyEducator || {}
    if (window.MyEducator.clinicalCaseDrafting) {
      window.MyEducator.clinicalCaseDrafting.destroy()
    }
    const ui = new ClinicalCaseDraftingUI(config)
    window.MyEducator.clinicalCaseDrafting = ui
    const payload = (config && config.payload) || {}
    ui.blueprint = ui.cloneJson(payload.blueprint || {})
    ui.beginRun(payload)
    ui.analytics.send('draft_v1_entry_opened', {}, 'entry-opened')
    return ui
  }

  constructor(config) {
    this.config = config || {}
    this.root = $(this.config.rootSelector || '.ai-clinical-case-root').last()
    this.modal = this.root.closest('.modal')
    this.modalBody = this.root.closest('.modal-body')
    this.analytics = new AssessmentDraftingAnalytics(this.config)

    this.phase = 'setup'
    this.active = false
    this.allowClose = false
    this.destroyed = false
    this.saving = false
    this.setupLoading = false
    this.requestGeneration = 0
    this.generationSlot = null

    this.setupUrl = ''
    this.setupData = ''
    this.blueprintToken = ''
    this.blueprint = null
    this.blueprintBindings = []
    this.acceptUrl = ''

    this.runHandle = ''
    this.slots = []
    this.currentIndex = 0
    this.candidates = {}
    this.failures = {}
    this.redraftFallbacks = {}
    this.acceptedSlots = {}
    this.candidateUrlTemplate = ''
    this.finalizeUrl = ''
    this.discardUrl = ''

    this.setupXhr = null
    this.acceptXhr = null
    this.generationXhr = null
    this.finalizeXhr = null
    this.discardXhr = null
    this.scrollGeneration = 0
    this.ckeditorReadyHandler = null
    this.ckeditorReadyTimers = []
    this.editorScrollObserver = null
    this.editorScrollQuietTimer = null
    this.editorScrollMaxTimer = null
    this.modalShownScrollHandler = null

    this.beforeUnloadHandler = event => {
      if (!this.active) return undefined
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
  }

  attachSetup() {
    if (!this.root.length) return
    this.analytics.send('draft_v1_entry_opened', {}, 'entry-opened')
    const form = this.root.find('form.ai-clinical-case-setup').first()
    this.setupUrl = form.attr('action') || ''
    this.modal
      .off('hidden.bs.modal.clinical_case_setup')
      .one('hidden.bs.modal.clinical_case_setup', () => {
        if (!this.allowClose) {
          this.analytics.send(
            'draft_v1_workflow_closed',
            { close_mode: 'closed' },
            'workflow-closed'
          )
        }
        this.destroy()
      })
    form.off('submit.clinical_case').on('submit.clinical_case', event => {
      event.preventDefault()
      this.requestBlueprint(form)
    })
    form
      .off('input.clinical_case_errors change.clinical_case_errors')
      .on(
        'input.clinical_case_errors change.clinical_case_errors',
        ':input',
        () => this.clearSetupErrors()
      )
    this.root
      .find('.ai-clinical-case-cancel')
      .off('click.clinical_case')
      .on('click.clinical_case', () => {
        this.analytics.send(
          'draft_v1_workflow_closed',
          { close_mode: 'closed' },
          'workflow-closed'
        )
        this.allowClose = true
        this.destroy()
        this.modal.modal('hide')
      })
  }

  requestBlueprint(form = null) {
    if (this.destroyed || this.setupXhr || this.acceptXhr) return
    if (form && !this.formIsValid(form)) return

    if (form) {
      this.setupUrl = form.attr('action') || this.setupUrl
      this.setupData = form.serialize()
      this.clearSetupErrors()
    }
    if (!this.setupUrl || !this.setupData) {
      this.showPhaseError('Clinical-case drafting could not be started.')
      return
    }

    this.setSetupLoading(true)
    this.setBlueprintBusy(true, 'Drafting a new blueprint…')
    this.setupXhr = $.ajax({
      url: this.setupUrl,
      type: 'POST',
      dataType: 'json',
      data: this.setupData,
    })
      .done(payload => {
        if (this.destroyed) return
        if (
          !payload ||
          payload.success === false ||
          !payload.blueprint_token ||
          !payload.blueprint ||
          !payload.accept_url ||
          !this.validBlueprint(payload.blueprint)
        ) {
          this.showPhaseError(
            (payload && payload.message) ||
              'A clinical-case blueprint could not be drafted.'
          )
          return
        }
        this.renderBlueprint(payload)
      })
      .fail((xhr, textStatus) => {
        if (this.destroyed || textStatus === 'abort') return
        const payload = xhr.responseJSON || {}
        this.applySetupFieldErrors(payload.errors || {})
        this.showPhaseError(
          this.errorWithReference(
            payload.message ||
              'A clinical-case blueprint could not be drafted.',
            payload.support_reference
          )
        )
      })
      .always(() => {
        this.setupXhr = null
        this.setSetupLoading(false)
        this.setBlueprintBusy(false)
      })
  }

  renderBlueprint(payload) {
    this.analytics.update({
      analyticsUrl: payload.analytics_url,
      workflowToken: payload.workflow_token,
    })
    this.blueprintToken = payload.blueprint_token
    this.blueprint = this.cloneJson(payload.blueprint)
    this.acceptUrl = payload.accept_url
    this.blueprintBindings = []
    this.phase = 'blueprint'
    this.active = true
    this.bindExitWarning()

    this.modalBody.html(`
      <div class="ai-clinical-case-blueprint">
        <header class="ai-clinical-case-page-header">
          <p class="ai-clinical-case-eyebrow">Clinical case blueprint</p>
          <h3>Review the case before drafting questions</h3>
          <p>
            Edit the clinical context and chart content now. The six stages and
            their question formats stay fixed for this run.
          </p>
        </header>
        <div class="alert alert-warning ai-clinical-case-safety-note" role="note">
          <span class="fa-solid fa-triangle-exclamation ai-clinical-case-safety-icon" aria-hidden="true"></span>
          <div>
            <strong>Clinician review is required</strong>
            <p>Verify clinical accuracy, safety, chart timing, and every answer key before enabling this case.</p>
          </div>
        </div>
        <form class="ai-clinical-case-blueprint-form">
          <section class="ai-clinical-case-card" aria-labelledby="ai-clinical-case-overview-heading">
            <h4 id="ai-clinical-case-overview-heading">Case overview</h4>
            ${this.blueprintFieldHtml(
              'Case title',
              ['case_title'],
              this.blueprint.case_title,
              { singleLine: true }
            )}
            ${this.blueprintFieldHtml(
              'Baseline stimulus',
              ['baseline_stimulus'],
              this.blueprint.baseline_stimulus
            )}
            <div class="ai-clinical-case-blueprint-grid">
              ${this.blueprintFieldHtml(
                'Client details',
                ['client_details'],
                this.blueprint.client_details
              )}
              ${this.blueprintFieldHtml(
                'Setting',
                ['setting'],
                this.blueprint.setting
              )}
              ${this.blueprintFieldHtml(
                'Presenting problem',
                ['presenting_problem'],
                this.blueprint.presenting_problem
              )}
            </div>
          </section>
          ${this.blueprintChartEditorHtml()}
          ${this.blueprintStagesHtml()}
          <div class="alert alert-danger ai-clinical-case-blueprint-error" role="alert" aria-live="assertive"></div>
          <div class="ai-clinical-case-local-progress" role="status" aria-live="polite"></div>
          <footer class="ai-clinical-case-actions">
            <button type="button" class="btn btn-myed btn-default btn-default-myed ai-clinical-case-draft-again">
              Draft again
            </button>
            <button type="submit" class="btn btn-myed btn-primary btn-primary-myed ai-clinical-case-accept-blueprint">
              Accept blueprint
            </button>
          </footer>
        </form>
      </div>
    `)

    const form = this.modalBody.find('.ai-clinical-case-blueprint-form')
    form.on('submit.clinical_case', event => {
      event.preventDefault()
      this.acceptBlueprint(form)
    })
    form.find('.ai-clinical-case-draft-again').on('click', () => {
      this.requestBlueprint()
    })
    this.scrollModalToTop()
  }

  blueprintFieldHtml(label, path, value, { singleLine = false } = {}) {
    const bindingId = this.blueprintBindings.length
    this.blueprintBindings.push({
      path: [...path],
      originalType: value === null ? 'null' : typeof value,
    })
    const id = `ai-clinical-case-blueprint-field-${bindingId}`
    const renderedValue = value == null ? '' : String(value)
    const control = singleLine
      ? `<input
          type="text"
          class="form-control ai-clinical-case-blueprint-field"
          id="${id}"
          data-binding-id="${bindingId}"
          value="${this.escapeAttribute(renderedValue)}"
          required
        >`
      : `<textarea
          class="form-control ai-clinical-case-blueprint-field"
          id="${id}"
          data-binding-id="${bindingId}"
          rows="3"
          required
        >${this.escapeHtml(renderedValue)}</textarea>`
    return `
      <div class="form-group ai-clinical-case-blueprint-field-group">
        <label for="${id}">${this.escapeHtml(label)}</label>
        ${control}
      </div>
    `
  }

  blueprintChartEditorHtml() {
    const sections = this.blueprint.chart_sections

    const sectionHtml = sections
      .map((section, sectionIndex) => {
        const sectionPath = ['chart_sections', sectionIndex]
        const entriesHtml = section.entries
          .map((entry, entryIndex) => {
            const label = this.chartEntryLabel(entry, entryIndex)
            return `
              <div class="ai-clinical-case-chart-entry-editor">
                ${this.blueprintFieldHtml(
                  label,
                  [...sectionPath, 'entries', entryIndex, 'content'],
                  entry.content
                )}
                ${this.chartEntryTimingHtml(entry)}
              </div>
            `
          })
          .join('')
        return `
          <article class="ai-clinical-case-chart-section-editor">
            ${this.blueprintFieldHtml(
              `Chart section ${sectionIndex + 1} title`,
              [...sectionPath, 'title'],
              section.title,
              { singleLine: true }
            )}
            <div class="ai-clinical-case-chart-entry-editors">
              ${
                entriesHtml ||
                '<p class="text-muted">No chart entries are planned for this section.</p>'
              }
            </div>
          </article>
        `
      })
      .join('')

    return `
      <section class="ai-clinical-case-card" aria-labelledby="ai-clinical-case-chart-heading">
        <div class="ai-clinical-case-section-heading">
          <div>
            <h4 id="ai-clinical-case-chart-heading">Patient chart</h4>
            <p>Edit section titles and entry content. Reveal timing stays fixed.</p>
          </div>
        </div>
        <div class="ai-clinical-case-chart-editor">
          ${
            sectionHtml ||
            '<p class="text-muted">No chart sections were returned.</p>'
          }
        </div>
      </section>
    `
  }

  blueprintStagesHtml() {
    return `
      <section class="ai-clinical-case-card" aria-labelledby="ai-clinical-case-stages-heading">
        <div class="ai-clinical-case-section-heading">
          <div>
            <h4 id="ai-clinical-case-stages-heading">Six-stage plan</h4>
            <p>Stage order and formats are fixed after the blueprint is drafted.</p>
          </div>
          <span class="ai-clinical-case-fixed-badge">
            <span class="fa-solid fa-lock ai-clinical-case-fixed-icon" aria-hidden="true"></span>
            Fixed
          </span>
        </div>
        <ol class="ai-clinical-case-blueprint-stages">
          ${this.normalizedBlueprintStages()
            .map(
              (stage, index) => `
                <li>
                  <span class="ai-clinical-case-stage-number">${
                    index + 1
                  }</span>
                  <span class="ai-clinical-case-stage-copy">
                    <strong>${this.escapeHtml(
                      this.stageLabel(stage, index)
                    )}</strong>
                    <span>${this.escapeHtml(
                      this.plannedFormatLabel(stage)
                    )}</span>
                  </span>
                </li>
              `
            )
            .join('')}
        </ol>
      </section>
    `
  }

  normalizedBlueprintStages() {
    const stages = Array.isArray(this.blueprint && this.blueprint.slots)
      ? this.blueprint.slots.slice(0, 6)
      : Array.isArray(this.blueprint && this.blueprint.stages)
      ? this.blueprint.stages.slice(0, 6)
      : []
    while (stages.length < 6) stages.push({})
    return stages
  }

  collectEditedBlueprint() {
    const edited = this.cloneJson(this.blueprint)
    this.modalBody
      .find('.ai-clinical-case-blueprint-field')
      .each((_index, element) => {
        const field = $(element)
        const binding =
          this.blueprintBindings[Number(field.attr('data-binding-id'))]
        if (!binding) return
        this.setAtPath(
          edited,
          binding.path,
          this.coerceValue(field.val(), binding.originalType)
        )
      })
    return edited
  }

  acceptBlueprint(form) {
    if (this.destroyed || this.acceptXhr || this.setupXhr) return
    if (!this.formIsValid(form)) return
    const editedBlueprint = this.collectEditedBlueprint()
    this.clearBlueprintError()
    this.setBlueprintBusy(true, 'Creating the disabled clinical case…')
    const data = {
      blueprint_token: this.blueprintToken,
      blueprint: JSON.stringify(editedBlueprint),
    }
    if (this.analytics.token) {
      data.drafting_workflow_token = this.analytics.token
    }
    this.acceptXhr = $.ajax({
      url: this.acceptUrl,
      type: 'POST',
      dataType: 'json',
      data,
    })
      .done(payload => {
        if (this.destroyed) return
        if (!payload || payload.success === false) {
          this.showBlueprintError(
            (payload && payload.message) ||
              'The clinical case could not be created.'
          )
          return
        }
        this.blueprint = this.cloneJson(payload.blueprint || editedBlueprint)
        this.beginRun(payload)
      })
      .fail((xhr, textStatus) => {
        if (this.destroyed || textStatus === 'abort') return
        const payload = xhr.responseJSON || {}
        this.showBlueprintError(
          this.errorWithReference(
            payload.message || 'The clinical case could not be created.',
            payload.support_reference
          )
        )
      })
      .always(() => {
        this.acceptXhr = null
        this.setBlueprintBusy(false)
      })
  }

  beginRun(payload) {
    const slots = Array.isArray(payload.slots) ? payload.slots : []
    if (
      slots.length !== 6 ||
      !payload.run_handle ||
      !payload.candidate_url_template ||
      !payload.finalize_url ||
      !payload.discard_url
    ) {
      this.showBlueprintError(
        'The case was created, but its six-stage drafting details were incomplete. Keep this window open and try again.'
      )
      return
    }

    this.runHandle = payload.run_handle
    this.slots = slots
    this.candidateUrlTemplate = payload.candidate_url_template
    this.finalizeUrl = payload.finalize_url
    this.discardUrl = payload.discard_url
    this.analytics.update({
      analyticsUrl: payload.analytics_url,
      workflowToken: payload.workflow_token,
    })
    this.candidates = {}
    this.failures = {}
    this.acceptedSlots = {}
    this.applyResumeMetadata(payload)
    this.currentIndex = this.nextUnacceptedIndex(payload)
    this.phase = 'run'
    this.active = true
    this.bindExitWarning()

    if (this.currentIndex >= this.slots.length) {
      this.finalize()
      return
    }
    this.renderGenerating(this.currentIndex)
    this.generate(this.currentIndex, { display: true })
  }

  applyResumeMetadata(payload) {
    ;(payload.accepted_slots || []).forEach(entry => {
      const position = this.slotPosition(entry.slot_index)
      if (position >= 0) {
        this.acceptedSlots[position] = this.acceptedMetadata(entry, position)
      }
    })
  }

  nextUnacceptedIndex(payload = {}) {
    const requested = payload.next_slot_index
    if (requested != null) {
      const position = this.slotPosition(requested)
      if (position >= 0 && !this.acceptedSlots[position]) return position
    }
    return this.slots.findIndex(
      (_slot, index) => !this.acceptedSlots[index]
    ) === -1
      ? this.slots.length
      : this.slots.findIndex((_slot, index) => !this.acceptedSlots[index])
  }

  generate(slotIndex, { display = false, previousCandidateToken = '' } = {}) {
    if (
      this.destroyed ||
      !this.active ||
      this.generationXhr ||
      !this.slots[slotIndex] ||
      this.acceptedSlots[slotIndex]
    ) {
      return
    }
    const generation = ++this.requestGeneration
    this.generationSlot = slotIndex
    if (display) this.renderGenerating(slotIndex)
    const data = {
      run_handle: this.runHandle,
      slot_index: this.slotRequestIndex(slotIndex),
    }
    if (this.analytics.token) {
      data.drafting_workflow_token = this.analytics.token
    }
    if (previousCandidateToken) {
      data.previous_candidate_token = previousCandidateToken
    }
    this.generationXhr = $.ajax({
      url: this.candidateUrl(slotIndex),
      type: 'POST',
      dataType: 'json',
      data,
    })
      .done(payload => {
        if (this.destroyed || generation !== this.requestGeneration) return
        if (!payload || payload.success === false || !payload.html) {
          this.recordGenerationFailure(slotIndex, payload || {})
          return
        }
        delete this.redraftFallbacks[slotIndex]
        delete this.failures[slotIndex]
        this.candidates[slotIndex] = payload
        if (payload.run_handle) this.runHandle = payload.run_handle
        if (slotIndex === this.currentIndex) this.renderCandidate(payload)
      })
      .fail((xhr, textStatus) => {
        if (
          this.destroyed ||
          generation !== this.requestGeneration ||
          textStatus === 'abort'
        ) {
          return
        }
        this.recordGenerationFailure(slotIndex, xhr.responseJSON || {})
      })
      .always(() => {
        if (generation !== this.requestGeneration) return
        this.generationXhr = null
        this.generationSlot = null
        if (this.candidates[this.currentIndex]) this.prefetchNext()
      })
  }

  recordGenerationFailure(slotIndex, payload) {
    const message = this.errorWithReference(
      payload.message || 'This stage could not be drafted.',
      payload.support_reference
    )
    const previous = this.redraftFallbacks[slotIndex]
    if (previous) {
      delete this.redraftFallbacks[slotIndex]
      delete this.failures[slotIndex]
      this.candidates[slotIndex] = previous
      if (slotIndex === this.currentIndex) {
        this.renderCandidate(previous)
        this.modalBody.find('.ai-clinical-case-run-error').text(message).show()
        this.scrollModalToTopAfterEditors()
      }
      return
    }
    this.failures[slotIndex] = { message }
    if (slotIndex === this.currentIndex) this.renderFailure(slotIndex)
  }

  prefetchNext() {
    const nextIndex = this.currentIndex + 1
    if (
      !this.active ||
      nextIndex >= this.slots.length ||
      this.acceptedSlots[nextIndex] ||
      this.candidates[nextIndex] ||
      this.failures[nextIndex] ||
      this.generationXhr
    ) {
      return
    }
    this.generate(nextIndex)
  }

  candidateUrl(slotIndex) {
    const slot = this.slots[slotIndex] || {}
    const questionType = slot.native_question_type || ''
    return String(this.candidateUrlTemplate || '')
      .replace(
        /__SLOT__/g,
        encodeURIComponent(this.slotRequestIndex(slotIndex))
      )
      .replace(/__TYPE__/g, encodeURIComponent(questionType))
      .replace(
        /__CONTRACT__/g,
        encodeURIComponent(slot.contract_key || questionType)
      )
  }

  renderGenerating(slotIndex) {
    this.renderRunShell(
      slotIndex,
      `
        <div class="ai-clinical-case-state" role="status" aria-live="polite">
          <span class="fa-solid fa-spinner fa-spin fa-2x" aria-hidden="true"></span>
          <h3>Drafting stage ${slotIndex + 1} of 6</h3>
          <p>${this.escapeHtml(
            this.stageLabel(this.slots[slotIndex], slotIndex)
          )}</p>
        </div>
      `
    )
  }

  renderCandidate(payload) {
    this.renderRunShell(this.currentIndex, payload.html)
    this.decorateCurrentEditor(payload)
    this.scrollModalToTopAfterEditors()
    this.prefetchNext()
  }

  renderRunShell(slotIndex, editorHtml) {
    this.modalBody.html(`
      <div class="ai-clinical-case-run">
        <aside class="ai-clinical-case-rail" aria-label="Clinical case progress and patient chart">
          ${this.stageRailHtml(slotIndex)}
          ${this.chartRailHtml(slotIndex)}
        </aside>
        <main class="ai-clinical-case-editor-pane">
          ${editorHtml || ''}
        </main>
      </div>
    `)
    this.scrollModalToTop()
  }

  stageRailHtml(currentIndex) {
    return `
      <section class="ai-clinical-case-stage-rail" aria-labelledby="ai-clinical-case-stage-rail-heading">
        <p class="ai-clinical-case-eyebrow">Case progress</p>
        <h3 id="ai-clinical-case-stage-rail-heading">Six clinical stages</h3>
        <ol>
          ${this.slots
            .map((slot, index) => {
              const accepted = Boolean(this.acceptedSlots[index])
              const failed = Boolean(this.failures[index])
              const current = index === currentIndex
              const status = accepted
                ? 'Accepted'
                : current
                ? failed
                  ? 'Needs retry'
                  : 'In review'
                : 'Waiting'
              return `
                <li class="${
                  accepted ? 'ai-clinical-case-stage-accepted' : ''
                } ${current ? 'ai-clinical-case-stage-current' : ''} ${
                failed ? 'ai-clinical-case-stage-error' : ''
              }" ${current ? 'aria-current="step"' : ''}>
                  <span class="ai-clinical-case-stage-marker ${
                    accepted ? 'ai-clinical-case-stage-marker-accepted' : ''
                  }" aria-hidden="true">
                    ${
                      accepted
                        ? '<span class="fa-solid fa-check"></span>'
                        : index + 1
                    }
                  </span>
                  <span class="ai-clinical-case-stage-copy">
                    <strong>${this.escapeHtml(
                      this.stageLabel(slot, index)
                    )}</strong>
                    <span>${this.escapeHtml(
                      this.plannedFormatLabel(slot)
                    )}</span>
                    <small>${status}</small>
                  </span>
                </li>
              `
            })
            .join('')}
        </ol>
      </section>
    `
  }

  chartRailHtml(slotIndex) {
    const sections = this.chartSectionsForSlot(slotIndex)
    const title = this.blueprint.case_title
    const baseline = this.blueprint.baseline_stimulus
    const sectionsHtml = sections
      .map((section, sectionIndex) => {
        const entries = section.entries
        return `
          <section class="ai-clinical-case-chart-section">
            <h4>${this.escapeHtml(
              section.title || `Chart section ${sectionIndex + 1}`
            )}</h4>
            <dl>
              ${entries
                .map((entry, entryIndex) => {
                  const label = this.chartEntryLabel(entry, entryIndex)
                  return `
                    <div class="ai-clinical-case-chart-row">
                      <dt>${this.escapeHtml(label)}</dt>
                      <dd>${this.escapeHtml(this.plainText(entry.content))}</dd>
                    </div>
                  `
                })
                .join('')}
            </dl>
          </section>
        `
      })
      .join('')
    return `
      <section class="ai-clinical-case-chart-rail" aria-labelledby="ai-clinical-case-chart-rail-heading">
        <p class="ai-clinical-case-eyebrow">Patient chart</p>
        <h3 id="ai-clinical-case-chart-rail-heading">${this.escapeHtml(
          title || 'Clinical case'
        )}</h3>
        ${
          baseline
            ? `<p class="ai-clinical-case-baseline">${this.escapeHtml(
                this.plainText(baseline)
              )}</p>`
            : ''
        }
        <div class="ai-clinical-case-chart-readonly" aria-readonly="true">
          ${
            sectionsHtml ||
            '<p class="text-muted">No chart entries are available at this stage.</p>'
          }
        </div>
      </section>
    `
  }

  chartSectionsForSlot(slotIndex) {
    const slot = this.slots[slotIndex] || {}
    const visibleIds = new Set(slot.visible_chart_entry_ids)
    return this.blueprint.chart_sections.map(section => {
      const entries = section.entries.filter(entry => visibleIds.has(entry.id))
      return { ...section, entries }
    })
  }

  chartEntryLabel(entry, index) {
    return `Entry ${index + 1}`
  }

  chartEntryTimingHtml(entry) {
    return `<p class="ai-clinical-case-chart-timing"><span class="fa-solid fa-lock ai-clinical-case-chart-timing-icon" aria-hidden="true"></span> Available from stage ${this.escapeHtml(
      entry.available_from_slot
    )}</p>`
  }

  decorateCurrentEditor(payload = null) {
    payload = payload || this.candidates[this.currentIndex]
    if (!payload || !this.active) return
    let form = this.modalBody.find('form').first()
    if (!form.length) return
    if (!this.modalBody.find('.ai-clinical-case-editor-pane').length) {
      form = form.detach()
      this.renderRunShell(this.currentIndex, '')
      this.modalBody.find('.ai-clinical-case-editor-pane').append(form)
    }

    const pane = this.modalBody.find('.ai-clinical-case-editor-pane')
    pane
      .find('.ai-clinical-case-editor-header, .ai-clinical-case-review-actions')
      .remove()
    form = pane.find('form').first()
    form.data('clinical-case-review', true)
    const nativeSaveLabel =
      form.find('.custom-form-submit-button').first().text().trim() || 'Save'
    form.find('.custom-form-submit-button').hide()

    const slot = this.slots[this.currentIndex] || {}
    this.lockNativeChartFields(form)

    const header = $(`
      <header class="ai-clinical-case-editor-header">
        <div>
          <p class="ai-clinical-case-eyebrow">Stage ${
            this.currentIndex + 1
          } of 6</p>
          <h3>${this.escapeHtml(this.stageLabel(slot, this.currentIndex))}</h3>
          <p>${this.escapeHtml(
            this.plannedFormatLabel(slot)
          )} · Review and edit before accepting</p>
        </div>
        <span class="ai-clinical-case-fixed-badge">
          <span class="fa-solid fa-lock ai-clinical-case-fixed-icon" aria-hidden="true"></span>
          Type and destination fixed
        </span>
        <div class="alert alert-danger ai-clinical-case-run-error" role="alert" aria-live="assertive"></div>
      </header>
    `)
    pane.prepend(header)

    const actions = $(`
      <footer class="ai-clinical-case-review-actions">
        <button type="button" class="btn btn-myed btn-default btn-default-myed ai-clinical-case-redraft">
          Redraft
        </button>
        <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-clinical-case-accept-candidate">
          Accept and continue
        </button>
      </footer>
    `)
    form.after(actions)
    actions.find('.ai-clinical-case-redraft').on('click', () => this.redraft())
    actions.find('.ai-clinical-case-accept-candidate').on('click', () => {
      if (this.saving || form.data('ajax_submitting')) return
      this.saving = true
      this.setReviewActionsDisabled(true)
      form
        .off('formlib.ajax_error.clinical_case')
        .one('formlib.ajax_error.clinical_case', (_event, _xhr, textStatus) => {
          if (textStatus === 'abort') {
            this.finishSaving()
            return
          }
          this.finishSaving()
          this.handleSaveError(
            'This stage could not be saved. Please try again.'
          )
        })
      const submitField = form.find('[name="submit_button"]')
      submitField.val(nativeSaveLabel)
      form.trigger('submit')
      if (!form.data('ajax_submitting')) {
        this.finishSaving()
        this.handleSaveError(
          'The native editor did not start saving. Please try again.'
        )
      }
    })
  }

  lockNativeChartFields(form) {
    const controls = form.find('[name]').filter((_index, element) => {
      const name = $(element).attr('name') || ''
      return (
        name === 'enable_tabs' ||
        name === 'tabs_widget_list' ||
        /^tabs_[0-9]+_[01]$/.test(name)
      )
    })
    const addedNames = new Set()
    controls.each((_index, element) => {
      const control = $(element)
      const name = control.attr('name')
      if (control.attr('type') === 'hidden') {
        control.addClass('ai-clinical-case-locked-chart-control')
        return
      }
      const isChoice = ['radio', 'checkbox'].includes(control.attr('type'))
      if (
        name &&
        !addedNames.has(name) &&
        (!isChoice || control.is(':checked'))
      ) {
        $('<input>', {
          type: 'hidden',
          name,
          value: control.val(),
          class:
            'ai-clinical-case-locked-control ai-clinical-case-locked-chart-control',
        }).appendTo(form)
        addedNames.add(name)
      }
      control
        .prop('disabled', true)
        .attr('aria-disabled', 'true')
        .closest('.form-group')
        .hide()
    })
  }

  handleSaveError(message) {
    this.finishSaving()
    if (!this.modalBody.find('.ai-clinical-case-run-error').length) {
      this.decorateCurrentEditor()
    }
    this.modalBody
      .find('.ai-clinical-case-run-error')
      .text(message || 'This stage could not be saved.')
      .show()
    this.scrollModalToTop()
  }

  handleInvalidSave() {
    this.finishSaving()
    this.decorateCurrentEditor()
    this.handleSaveError(
      'Check the highlighted fields and accept this stage again.'
    )
  }

  handleAccepted(payload) {
    this.finishSaving()
    if (!this.active || !payload) return
    const position = this.slotPosition(payload.slot_index)
    if (position !== this.currentIndex || this.acceptedSlots[position]) return
    if (payload.run_handle) this.runHandle = payload.run_handle
    this.acceptedSlots[position] = this.acceptedMetadata(payload, position)
    delete this.candidates[position]
    delete this.failures[position]
    this.advance()
  }

  acceptedMetadata(payload, position) {
    return {
      slot_index: this.slotRequestIndex(position),
      question_guid: payload.question_guid || '',
      editor_url: payload.editor_url || '',
      question_type_label:
        payload.question_type_label ||
        this.plannedFormatLabel(this.slots[position]),
      stage_label:
        payload.stage_label || this.stageLabel(this.slots[position], position),
    }
  }

  redraft() {
    if (this.saving || !this.active) return
    this.analytics.send(
      'draft_v1_redraft_requested',
      { slot: this.slotRequestIndex(this.currentIndex) + 1, attempt: 'redraft' },
      `redraft:${this.slotRequestIndex(this.currentIndex)}:${this.requestGeneration + 1}`
    )
    const previousCandidateToken =
      (this.candidates[this.currentIndex] || {}).candidate_token || ''
    const previousCandidate = this.candidates[this.currentIndex]
    this.abortGeneration()
    if (previousCandidate) {
      this.redraftFallbacks[this.currentIndex] = previousCandidate
    }
    delete this.failures[this.currentIndex]
    this.generate(this.currentIndex, {
      display: true,
      previousCandidateToken,
    })
  }

  retry(slotIndex = this.currentIndex) {
    if (this.saving || !this.active) return
    this.analytics.send(
      'draft_v1_candidate_retry_requested',
      { slot: this.slotRequestIndex(slotIndex) + 1 },
      `retry:${this.slotRequestIndex(slotIndex)}:${this.requestGeneration + 1}`
    )
    delete this.failures[slotIndex]
    this.generate(slotIndex, { display: true })
  }

  advance() {
    this.currentIndex += 1
    while (
      this.currentIndex < this.slots.length &&
      this.acceptedSlots[this.currentIndex]
    ) {
      this.currentIndex += 1
    }
    if (this.currentIndex >= this.slots.length) {
      this.finalize()
      return
    }
    if (this.candidates[this.currentIndex]) {
      this.renderCandidate(this.candidates[this.currentIndex])
    } else if (this.failures[this.currentIndex]) {
      this.renderFailure(this.currentIndex)
    } else if (this.generationSlot === this.currentIndex) {
      this.renderGenerating(this.currentIndex)
    } else {
      this.generate(this.currentIndex, { display: true })
    }
  }

  renderFailure(slotIndex) {
    const failure = this.failures[slotIndex] || {}
    this.renderRunShell(
      slotIndex,
      `
        <div class="ai-clinical-case-state">
          <span class="fa-solid fa-circle-exclamation fa-2x text-danger" aria-hidden="true"></span>
          <h3>Stage ${slotIndex + 1} could not be drafted</h3>
          <div class="alert alert-danger ai-clinical-case-state-alert" role="alert">${this.escapeHtml(
            failure.message
          )}</div>
          <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-clinical-case-retry">
            Retry
          </button>
        </div>
      `
    )
    this.modalBody.find('.ai-clinical-case-retry').on('click', () => {
      this.retry(slotIndex)
    })
  }

  finalize() {
    if (this.destroyed || this.finalizeXhr) return
    this.abortGeneration()
    this.phase = 'finalizing'
    this.renderLocalState(
      'Finalizing the clinical case',
      'Validating all six stages and saving the case as disabled.'
    )
    const data = { run_handle: this.runHandle }
    if (this.analytics.token) {
      data.drafting_workflow_token = this.analytics.token
    }
    this.finalizeXhr = $.ajax({
      url: this.finalizeUrl,
      type: 'POST',
      dataType: 'json',
      data,
    })
      .done(payload => {
        if (this.destroyed) return
        if (!payload || payload.success === false) {
          this.renderFinalizeFailure(
            (payload && payload.message) ||
              'The clinical case could not be finalized.'
          )
          return
        }
        if (payload.run_handle) this.runHandle = payload.run_handle
        this.renderFinalSummary(payload)
      })
      .fail((xhr, textStatus) => {
        if (this.destroyed || textStatus === 'abort') return
        const payload = xhr.responseJSON || {}
        this.renderFinalizeFailure(
          this.errorWithReference(
            payload.message || 'The clinical case could not be finalized.',
            payload.support_reference
          )
        )
      })
      .always(() => {
        this.finalizeXhr = null
      })
  }

  renderFinalizeFailure(message) {
    this.phase = 'run'
    this.modalBody.html(`
      <div class="ai-clinical-case-completion-state">
        <span class="fa-solid fa-circle-exclamation fa-2x text-danger" aria-hidden="true"></span>
        <h3>The questions are saved, but finalization needs another try</h3>
        <div class="alert alert-danger ai-clinical-case-state-alert" role="alert">${this.escapeHtml(
          message
        )}</div>
        <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-clinical-case-finalize-retry">
          Retry finalization
        </button>
      </div>
    `)
    this.modalBody.find('.ai-clinical-case-finalize-retry').on('click', () => {
      this.finalize()
    })
    this.scrollModalToTop()
  }

  renderFinalSummary(payload) {
    this.phase = 'complete'
    this.active = false
    this.unbindExitWarning()
    const returnedSlots = Array.isArray(payload.slots) ? payload.slots : []
    returnedSlots.forEach(entry => {
      const position = this.slotPosition(entry.slot_index)
      if (position >= 0) {
        this.acceptedSlots[position] = this.acceptedMetadata(entry, position)
      }
    })
    const title = payload.case_title || this.blueprint.case_title
    const caseUrl = payload.case_url || ''
    this.modalBody.html(`
      <div class="ai-clinical-case-summary">
        <span class="ai-clinical-case-summary-icon fa-solid fa-circle-check" aria-hidden="true"></span>
        <p class="ai-clinical-case-eyebrow">Clinical case complete</p>
        <h3>${this.escapeHtml(title)}</h3>
        <div class="alert alert-info ai-clinical-case-summary-alert" role="status">
          <strong>Saved as disabled.</strong>
          Review the complete case and enable it only after clinical approval.
        </div>
        <ol class="ai-clinical-case-summary-stages">
          ${this.slots
            .map((slot, index) => {
              const accepted = this.acceptedSlots[index] || {}
              return `
                <li>
                  <span class="ai-clinical-case-stage-number">${
                    index + 1
                  }</span>
                  <span class="ai-clinical-case-stage-copy">
                    <strong>${this.escapeHtml(
                      this.stageLabel(slot, index)
                    )}</strong>
                    <span>${this.escapeHtml(
                      accepted.question_type_label ||
                        this.plannedFormatLabel(slot)
                    )}</span>
                  </span>
                  ${
                    accepted.editor_url
                      ? `
                    <button
                      type="button"
                      class="btn btn-link btn-sm ai-clinical-case-reopen"
                      data-url="${this.escapeAttribute(accepted.editor_url)}"
                    >Reopen</button>
                  `
                      : ''
                  }
                </li>
              `
            })
            .join('')}
        </ol>
        <footer class="ai-clinical-case-actions ai-clinical-case-summary-actions">
          ${
            caseUrl
              ? `<a class="btn btn-myed btn-default btn-default-myed" href="${this.escapeAttribute(
                  caseUrl
                )}">Review disabled case</a>`
              : ''
          }
          <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-clinical-case-done">Done</button>
        </footer>
      </div>
    `)
    this.modalBody.find('.ai-clinical-case-done').on('click', () => {
      this.allowClose = true
      this.modal.modal('hide')
    })
    this.modalBody.find('.ai-clinical-case-reopen').on('click', event => {
      this.openQuestionEditor($(event.currentTarget).attr('data-url'))
    })
    this.scrollModalToTop()
  }

  openQuestionEditor(url) {
    if (!url) return
    this.allowClose = true
    this.modal.one('hidden.bs.modal.clinical_case_reopen', () => {
      if ($.meDialog && typeof $.meDialog.open2 === 'function') {
        $.meDialog.open2({
          id: 'question_editor_dialog',
          url,
          title: 'Question Editor',
          width: 900,
        })
      }
    })
    this.modal.modal('hide')
  }

  bindExitWarning() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
    this.modal
      .off('hide.bs.modal.clinical_case')
      .on('hide.bs.modal.clinical_case', event => {
        if (!this.active || this.allowClose) return
        if (this.hasBlockingRequest()) {
          event.preventDefault()
          this.showPhaseError(
            'Wait for the current request to finish before closing.'
          )
          return
        }
        if (!this.runHandle || !this.discardUrl) {
          if (
            !window.confirm(
              'Close this clinical-case blueprint without accepting it?'
            )
          ) {
            event.preventDefault()
            return
          }
          this.analytics.send(
            'draft_v1_workflow_closed',
            { close_mode: 'closed' },
            'workflow-closed'
          )
          this.destroy()
          return
        }

        const keep = window.confirm(
          'Keep this unfinished clinical case for later? Select OK to keep it, or Cancel for discard options.'
        )
        if (keep) {
          this.analytics.send(
            'draft_v1_workflow_closed',
            { close_mode: 'keep_for_later' },
            'workflow-closed:keep-for-later'
          )
          this.allowClose = true
          this.destroy()
          return
        }
        event.preventDefault()
        if (
          !window.confirm(
            'Discard this unfinished clinical case and its accepted questions?'
          )
        ) {
          return
        }
        this.discardRun()
      })
  }

  unbindExitWarning() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    this.modal.off('hide.bs.modal.clinical_case')
  }

  discardRun() {
    if (this.destroyed || this.discardXhr || !this.discardUrl) return
    this.abortGeneration()
    this.phase = 'discarding'
    this.renderLocalState(
      'Discarding the unfinished case',
      'Removing the disabled draft and its accepted questions.'
    )
    const data = { run_handle: this.runHandle }
    if (this.analytics.token) {
      data.drafting_workflow_token = this.analytics.token
    }
    this.discardXhr = $.ajax({
      url: this.discardUrl,
      type: 'POST',
      dataType: 'json',
      data,
    })
      .done(payload => {
        if (this.destroyed) return
        if (!payload || payload.success === false) {
          this.renderDiscardFailure(
            (payload && payload.message) ||
              'The unfinished case could not be discarded.'
          )
          return
        }
        this.active = false
        this.allowClose = true
        this.destroy(false)
        this.modal.modal('hide')
      })
      .fail((xhr, textStatus) => {
        if (this.destroyed || textStatus === 'abort') return
        const payload = xhr.responseJSON || {}
        this.renderDiscardFailure(
          this.errorWithReference(
            payload.message || 'The unfinished case could not be discarded.',
            payload.support_reference
          )
        )
      })
      .always(() => {
        this.discardXhr = null
      })
  }

  renderDiscardFailure(message) {
    this.phase = 'run'
    this.modalBody.html(`
      <div class="ai-clinical-case-completion-state">
        <span class="fa-solid fa-circle-exclamation fa-2x text-danger" aria-hidden="true"></span>
        <h3>The unfinished case was not discarded</h3>
        <div class="alert alert-danger ai-clinical-case-state-alert" role="alert">${this.escapeHtml(
          message
        )}</div>
        <div class="ai-clinical-case-actions">
          <button type="button" class="btn btn-myed btn-default btn-default-myed ai-clinical-case-keep-later">Keep for later</button>
          <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-clinical-case-discard-retry">Retry discard</button>
        </div>
      </div>
    `)
    this.modalBody.find('.ai-clinical-case-keep-later').on('click', () => {
      this.analytics.send(
        'draft_v1_workflow_closed',
        { close_mode: 'keep_for_later' },
        'workflow-closed:keep-for-later'
      )
      this.allowClose = true
      this.destroy()
      this.modal.modal('hide')
    })
    this.modalBody.find('.ai-clinical-case-discard-retry').on('click', () => {
      this.discardRun()
    })
    this.scrollModalToTop()
  }

  renderLocalState(title, description) {
    this.modalBody.html(`
      <div class="ai-clinical-case-completion-state" role="status" aria-live="polite">
        <span class="fa-solid fa-spinner fa-spin fa-2x" aria-hidden="true"></span>
        <h3>${this.escapeHtml(title)}</h3>
        <p>${this.escapeHtml(description)}</p>
      </div>
    `)
    this.scrollModalToTop()
  }

  hasBlockingRequest() {
    return Boolean(
      this.saving ||
        this.setupXhr ||
        this.acceptXhr ||
        this.finalizeXhr ||
        this.discardXhr
    )
  }

  abortGeneration() {
    this.requestGeneration += 1
    const xhr = this.generationXhr
    this.generationXhr = null
    this.generationSlot = null
    if (xhr && typeof xhr.abort === 'function') xhr.abort()
  }

  finishSaving() {
    this.saving = false
    this.setReviewActionsDisabled(false)
  }

  setReviewActionsDisabled(disabled) {
    this.modalBody
      .find('.ai-clinical-case-editor-pane form')
      .attr('aria-busy', disabled ? 'true' : 'false')
    this.modalBody
      .find('.ai-clinical-case-review-actions button')
      .prop('disabled', disabled)
  }

  destroy(abort = true) {
    if (this.destroyed) return
    this.destroyed = true
    this.active = false
    this.saving = false
    this.unbindExitWarning()
    if (abort) {
      this.abortXhr('setupXhr')
      this.abortXhr('acceptXhr')
      this.abortGeneration()
      this.abortXhr('finalizeXhr')
      this.abortXhr('discardXhr')
    }
    this.setReviewActionsDisabled(false)
    this.setupData = ''
    this.blueprintToken = ''
    this.blueprint = null
    this.blueprintBindings = []
    this.runHandle = ''
    this.slots = []
    this.candidates = {}
    this.failures = {}
    this.redraftFallbacks = {}
    this.acceptedSlots = {}
    this.candidateUrlTemplate = ''
    this.finalizeUrl = ''
    this.discardUrl = ''
    this.clearDeferredEditorScroll()
    if (window.MyEducator.clinicalCaseDrafting === this) {
      window.MyEducator.clinicalCaseDrafting = null
    }
  }

  abortXhr(property) {
    const xhr = this[property]
    this[property] = null
    if (xhr && typeof xhr.abort === 'function') xhr.abort()
  }

  setSetupLoading(loading) {
    this.setupLoading = loading
    this.root
      .find('form.ai-clinical-case-setup')
      .attr('aria-busy', loading ? 'true' : 'false')
    this.root.find('.ai-clinical-case-progress').toggle(loading)
    this.root
      .find('form.ai-clinical-case-setup :input')
      .not('.ai-clinical-case-cancel')
      .prop('disabled', loading)
  }

  setBlueprintBusy(busy, message = '') {
    const form = this.modalBody.find('.ai-clinical-case-blueprint-form')
    form.attr('aria-busy', busy ? 'true' : 'false')
    form.find('button').prop('disabled', busy)
    form
      .find('.ai-clinical-case-local-progress')
      .text(busy ? message : '')
      .toggle(Boolean(busy))
  }

  clearSetupErrors() {
    this.root.find('.ai-clinical-case-error').hide().empty()
    this.root.find('.ai-clinical-case-field-error').empty()
  }

  applySetupFieldErrors(errors) {
    const fieldErrors = this.root.find('.ai-clinical-case-field-error')
    Object.keys(errors).forEach(field => {
      fieldErrors
        .filter((_index, element) => $(element).attr('data-field') === field)
        .text((errors[field] || []).join(' '))
    })
  }

  showPhaseError(message) {
    if (this.phase === 'setup') {
      this.root.find('.ai-clinical-case-error').text(message).show()
    } else if (this.phase === 'blueprint') {
      this.showBlueprintError(message)
    } else {
      const target = this.modalBody.find('.ai-clinical-case-run-error')
      if (target.length) target.text(message).show()
    }
    this.scrollModalToTop()
  }

  showBlueprintError(message) {
    this.modalBody
      .find('.ai-clinical-case-blueprint-error')
      .text(message)
      .show()
    this.scrollModalToTop()
  }

  clearBlueprintError() {
    this.modalBody.find('.ai-clinical-case-blueprint-error').hide().empty()
  }

  formIsValid(form) {
    const element = form && form[0]
    if (!element || typeof element.checkValidity !== 'function') return true
    if (element.checkValidity()) return true
    if (typeof element.reportValidity === 'function') element.reportValidity()
    return false
  }

  slotRequestIndex(position) {
    const slot = this.slots[position] || {}
    return slot.slot_index != null ? slot.slot_index : position
  }

  slotPosition(serverIndex) {
    const exact = this.slots.findIndex(
      (slot, position) =>
        String(this.slotRequestIndex(position)) === String(serverIndex)
    )
    return exact
  }

  stageLabel(stage, index) {
    const value =
      (stage && stage.stage) ||
      DEFAULT_STAGE_LABELS[index] ||
      `Stage ${index + 1}`
    return this.humanize(value)
  }

  plannedFormatLabel(stage) {
    if (!stage) return 'Planned question format'
    if (FORMAT_LABELS[stage.contract_key]) {
      return FORMAT_LABELS[stage.contract_key]
    }
    const value = stage.question_type_label || stage.contract_key
    if (!value) return 'Planned question format'
    return this.humanize(value)
  }

  validBlueprint(blueprint) {
    if (!blueprint || typeof blueprint !== 'object') return false
    if (
      !this.hasExactKeys(blueprint, [
        'case_title',
        'baseline_stimulus',
        'client_details',
        'setting',
        'presenting_problem',
        'chart_sections',
        'slots',
      ]) ||
      ![
        blueprint.case_title,
        blueprint.baseline_stimulus,
        blueprint.client_details,
        blueprint.setting,
        blueprint.presenting_problem,
      ].every(value => typeof value === 'string')
    ) {
      return false
    }
    const sections = blueprint.chart_sections
    const slots = blueprint.slots
    return Boolean(
      Array.isArray(sections) &&
        sections.length >= 1 &&
        sections.length <= 6 &&
        sections.every(
          section =>
            this.hasExactKeys(section, ['id', 'title', 'entries']) &&
            typeof section.id === 'string' &&
            typeof section.title === 'string' &&
            Array.isArray(section.entries) &&
            section.entries.length >= 1 &&
            section.entries.every(
              entry =>
                this.hasExactKeys(entry, [
                  'id',
                  'content',
                  'available_from_slot',
                  'source_page_ids',
                ]) &&
                typeof entry.id === 'string' &&
                typeof entry.content === 'string' &&
                Number.isInteger(entry.available_from_slot) &&
                Array.isArray(entry.source_page_ids)
            )
        ) &&
        Array.isArray(slots) &&
        slots.length === 6 &&
        slots.every(
          slot =>
            this.hasExactKeys(slot, [
              'stage',
              'contract_key',
              'intent',
              'source_page_ids',
              'visible_chart_entry_ids',
            ]) &&
            typeof slot.stage === 'string' &&
            typeof slot.contract_key === 'string' &&
            typeof slot.intent === 'string' &&
            Array.isArray(slot.source_page_ids) &&
            Array.isArray(slot.visible_chart_entry_ids)
        )
    )
  }

  hasExactKeys(value, keys) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return false
    const actual = Object.keys(value).sort()
    const expected = keys.slice().sort()
    return (
      actual.length === expected.length &&
      actual.every((key, index) => key === expected[index])
    )
  }

  setAtPath(target, path, value) {
    let cursor = target
    path.forEach((key, index) => {
      if (index === path.length - 1) {
        cursor[key] = value
        return
      }
      if (cursor[key] == null || typeof cursor[key] !== 'object') {
        cursor[key] = typeof path[index + 1] === 'number' ? [] : {}
      }
      cursor = cursor[key]
    })
  }

  coerceValue(value, originalType) {
    if (originalType === 'number') {
      const number = Number(value)
      return Number.isFinite(number) ? number : value
    }
    if (originalType === 'boolean')
      return String(value).toLowerCase() === 'true'
    return value == null ? '' : String(value)
  }

  cloneJson(value) {
    return JSON.parse(JSON.stringify(value))
  }

  humanize(value) {
    return String(value == null ? '' : value)
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, letter => letter.toUpperCase())
  }

  plainText(value) {
    const parsed = new window.DOMParser().parseFromString(
      String(value == null ? '' : value),
      'text/html'
    )
    return parsed.body.textContent || ''
  }

  errorWithReference(message, reference) {
    if (!reference || String(message).includes(reference)) return message
    return `${message} Support reference: ${reference}.`
  }

  scrollModalToTop() {
    // Bootstrap owns the visible modal scrollbar; modalBody can also become a
    // scroller in legacy dialog variants. Reset both so a render never inherits
    // the blueprint or prior editor's position.
    this.modal.scrollTop(0)
    this.modalBody.scrollTop(0)
  }

  scrollModalToTopAfterEditors() {
    this.clearDeferredEditorScroll()
    const generation = ++this.scrollGeneration
    const scroll = () => {
      if (!this.destroyed && generation === this.scrollGeneration) {
        this.scrollModalToTop()
      }
    }
    this.modalShownScrollHandler = scroll
    this.modal.one(
      'shown.bs.modal.clinical_case_scroll',
      this.modalShownScrollHandler
    )
    scroll()
    ;[0, 250, 1000, 2500].forEach(delay => {
      this.ckeditorReadyTimers.push(window.setTimeout(scroll, delay))
    })

    // CKEditor replaces each textarea asynchronously. Those mutations can move
    // the modal after the first scroll reset, especially when a question has
    // several chart tabs. Keep resetting until the editor pane has been quiet
    // briefly, with a hard stop so ordinary author interaction is unaffected.
    if (window.MutationObserver && this.modalBody[0]) {
      const stopObserving = () => {
        if (this.editorScrollObserver) {
          this.editorScrollObserver.disconnect()
          this.editorScrollObserver = null
        }
        if (this.editorScrollQuietTimer) {
          window.clearTimeout(this.editorScrollQuietTimer)
          this.editorScrollQuietTimer = null
        }
        if (this.editorScrollMaxTimer) {
          window.clearTimeout(this.editorScrollMaxTimer)
          this.editorScrollMaxTimer = null
        }
        scroll()
      }
      const observedMutation = () => {
        scroll()
        if (this.editorScrollQuietTimer) {
          window.clearTimeout(this.editorScrollQuietTimer)
        }
        this.editorScrollQuietTimer = window.setTimeout(stopObserving, 750)
      }
      this.editorScrollObserver = new window.MutationObserver(observedMutation)
      this.editorScrollObserver.observe(this.modalBody[0], {
        childList: true,
        subtree: true,
      })
      this.editorScrollMaxTimer = window.setTimeout(stopObserving, 5000)
    }

    const ckeditor = window.CKEDITOR
    const editorIds = new Set(
      this.modalBody
        .find('.ai-clinical-case-editor-pane textarea[id]')
        .toArray()
        .map(element => element.id)
    )
    if (!editorIds.size || !ckeditor || typeof ckeditor.on !== 'function') {
      return
    }
    const pendingEditorIds = new Set(editorIds)
    this.ckeditorReadyHandler = event => {
      const editorName = event && event.editor && event.editor.name
      if (!pendingEditorIds.has(editorName)) return
      pendingEditorIds.delete(editorName)
      window.setTimeout(scroll, 0)
      if (
        !pendingEditorIds.size &&
        this.ckeditorReadyHandler &&
        typeof ckeditor.removeListener === 'function'
      ) {
        ckeditor.removeListener('instanceReady', this.ckeditorReadyHandler)
        this.ckeditorReadyHandler = null
      }
    }
    ckeditor.on('instanceReady', this.ckeditorReadyHandler)
  }

  clearDeferredEditorScroll(invalidate = true) {
    if (invalidate) this.scrollGeneration += 1
    if (this.modalShownScrollHandler) {
      this.modal.off(
        'shown.bs.modal.clinical_case_scroll',
        this.modalShownScrollHandler
      )
      this.modalShownScrollHandler = null
    }
    if (
      this.ckeditorReadyHandler &&
      window.CKEDITOR &&
      typeof window.CKEDITOR.removeListener === 'function'
    ) {
      window.CKEDITOR.removeListener('instanceReady', this.ckeditorReadyHandler)
    }
    this.ckeditorReadyHandler = null
    this.ckeditorReadyTimers.forEach(timer => window.clearTimeout(timer))
    this.ckeditorReadyTimers = []
    if (this.editorScrollObserver) {
      this.editorScrollObserver.disconnect()
      this.editorScrollObserver = null
    }
    if (this.editorScrollQuietTimer) {
      window.clearTimeout(this.editorScrollQuietTimer)
      this.editorScrollQuietTimer = null
    }
    if (this.editorScrollMaxTimer) {
      window.clearTimeout(this.editorScrollMaxTimer)
      this.editorScrollMaxTimer = null
    }
  }

  escapeHtml(value) {
    return $('<div>')
      .text(value == null ? '' : String(value))
      .html()
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
}
